import React, { useMemo, useState } from 'react';
import { Card } from 'primereact/card';
import { Panel } from 'primereact/panel';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Message } from 'primereact/message';
import { TabView, TabPanel } from 'primereact/tabview';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Chart } from 'primereact/chart';
import { NoRecordsCard } from './_SmartFieldRenderer';

/* ─── formatting ─────────────────────────────────────────────────────── */

// Indian-standard money formatting (mandatory — ui-ux skill).
const formatINR = (amount) => {
    const n = parseFloat(amount);
    if (!Number.isFinite(n)) return '—';
    return `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
};

const formatNum = (n) => {
    const v = parseFloat(n);
    return Number.isFinite(v) ? v.toLocaleString('en-IN') : '—';
};

// Compact form for chart axes: ₹1.2Cr, ₹45.6L, ₹78.9K
const formatCompact = (amount) => {
    const n = parseFloat(amount);
    if (!Number.isFinite(n)) return '—';
    const abs = Math.abs(n);
    if (abs >= 1e7) return `₹${(n / 1e7).toFixed(2)}Cr`;
    if (abs >= 1e5) return `₹${(n / 1e5).toFixed(2)}L`;
    if (abs >= 1e3) return `₹${(n / 1e3).toFixed(1)}K`;
    return formatINR(n);
};

// "072026" -> "Jul 2026". The vendor sends MMYYYY with no separator.
const formatPeriod = (mmyyyy) => {
    const s = String(mmyyyy || '').trim();
    if (!/^\d{6}$/.test(s)) return s || '—';
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const m = parseInt(s.slice(0, 2), 10);
    return `${months[m - 1] || s.slice(0, 2)} ${s.slice(2)}`;
};

const MONTH_ORDER = ['Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar'];
const MONTH_IDX = { jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5, jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11 };
const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Vendor month maps are unordered and keyed by short month name. Sort to the
// Indian financial year so charts read Apr → Mar rather than alphabetically.
const sortFyMonths = (obj) =>
    Object.keys(obj || {}).sort((a, b) => MONTH_ORDER.indexOf(a) - MONTH_ORDER.indexOf(b));

// Attach the YEAR to each fiscal-year-ordered month using the filing period
// (fromPeriod = MMYYYY, e.g. "042024" = Apr 2024 fiscal start). Returns
// [{ key: 'Apr', label: 'Apr-2024' }, …] so charts/tables can show the real year
// while still looking values up by the vendor's bare month key. Falls back to the
// bare month name when the period can't be parsed.
const fyMonthsWithYear = (obj, fromPeriod) => {
    const keys = sortFyMonths(obj);
    const fp = String(fromPeriod || '');
    const startMonth = fp.length >= 6 ? parseInt(fp.slice(0, 2), 10) : NaN; // 1-12
    const startYear = fp.length >= 6 ? parseInt(fp.slice(2), 10) : NaN;
    if (!Number.isFinite(startMonth) || !Number.isFinite(startYear)) {
        return keys.map((k) => ({ key: k, label: k }));
    }
    return keys.map((k) => {
        const idx = MONTH_IDX[String(k).slice(0, 3).toLowerCase()];
        if (idx == null) return { key: k, label: k };
        // Months from the fiscal start month roll the year over once they wrap past Dec.
        const offset = (idx - (startMonth - 1) + 12) % 12;
        const yr = startYear + Math.floor(((startMonth - 1) + offset) / 12);
        return { key: k, label: `${MONTH_SHORT[idx]}-${yr}` };
    });
};

/* ─── shared bits ────────────────────────────────────────────────────── */

const StatTile = ({ icon, label, value, sub, severity }) => (
    <div className="col-12 md:col-6 lg:col-3">
        <div className="card mb-0 h-full">
            <div className="flex justify-content-between align-items-start">
                <div className="flex flex-column gap-2 min-w-0">
                    <span className="text-600 text-sm font-medium">{label}</span>
                    <span className={`text-xl font-semibold ${severity === 'danger' ? 'text-red-500' : severity === 'success' ? 'text-green-600' : 'text-900'}`}>
                        {value}
                    </span>
                </div>
                <div className="flex align-items-center justify-content-center border-round bg-primary-50 p-2 flex-shrink-0">
                    <i className={`pi ${icon} text-xl text-primary`} aria-hidden="true" />
                </div>
            </div>
            {sub && <span className="text-500 text-xs block mt-2">{sub}</span>}
        </div>
    </div>
);

const InfoItem = ({ label, value, money }) => (
    <div className="col-12 md:col-6 lg:col-3">
        <div className="surface-100 border-round p-3 h-full">
            <div className="text-600 text-xs font-medium mb-1">{label}</div>
            <div className="text-900 text-base font-semibold" style={{ wordBreak: 'break-word' }}>
                {money ? formatINR(value) : (value || value === 0 ? value : '—')}
            </div>
        </div>
    </div>
);

const chartOptions = () => ({
    maintainAspectRatio: false,
    plugins: {
        legend: { labels: { color: 'var(--text-color-secondary)', usePointStyle: true, boxWidth: 8 } },
        tooltip: {
            callbacks: { label: (ctx) => `${ctx.dataset.label || ''}: ${formatINR(ctx.parsed.y ?? ctx.parsed)}` },
        },
    },
    scales: {
        x: { ticks: { color: 'var(--text-color-secondary)' }, grid: { color: 'var(--surface-border)' } },
        y: {
            ticks: { color: 'var(--text-color-secondary)', callback: (v) => formatCompact(v) },
            grid: { color: 'var(--surface-border)' },
        },
    },
});

// Renders any vendor list-of-objects as a paginated table. Columns come from the
// first row so a new field shows up rather than being silently dropped.
const AutoTable = ({ rows, moneyKeys = [], emptyText = 'No records', note }) => {
    if (!Array.isArray(rows) || rows.length === 0) {
        return (
            <div className="flex flex-column align-items-center py-4">
                <i className="pi pi-inbox pi-2x text-400 mb-2" aria-hidden="true" />
                <span className="text-600 text-sm">{emptyText}</span>
            </div>
        );
    }

    const cols = Object.keys(rows[0]);
    const money = new Set(moneyKeys);
    // Treat a column as currency when every value parses as a number and the
    // caller flagged it — avoids formatting GSTINs or dates as rupees.
    const bodyFor = (key) => (row) => {
        const v = row[key];
        if (v === null || v === undefined || v === '') return <span className="text-500">—</span>;
        if (money.has(key)) return <span className="font-medium">{formatINR(v)}</span>;
        return <span className="text-900">{String(v)}</span>;
    };

    return (
        <>
            {note && <p className="text-500 text-xs mt-0 mb-2">{note}</p>}
            <DataTable
                value={rows}
                paginator={rows.length > 10}
                rows={10}
                rowsPerPageOptions={[10, 25, 50]}
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport"
                currentPageReportTemplate="Showing {first}-{last} of {totalRecords}"
                paginatorClassName="text-sm"
                pageLinkSize={3}
                size="small"
                stripedRows
                showGridlines
                scrollable
                scrollHeight="flex"
                responsiveLayout="scroll"
                className="p-datatable-gridlines"
            >
                {cols.map((c, i) => (
                    <Column
                        key={c}
                        field={c}
                        header={c}
                        body={bodyFor(c)}
                        sortable
                        frozen={i === 0}
                        alignFrozen="left"
                        style={{ minWidth: i === 0 ? '160px' : '130px' }}
                    />
                ))}
            </DataTable>
        </>
    );
};

/**
 * GST Return Fetch — output for the borrower-consent payload.
 *
 * IMPORTANT: this is NOT the same shape as the GST upload flow. When the DSA
 * uploads GSTR-3B PDFs the vendor returns per-return tables
 * (`inward_and_outward_supplies`, `eligible_itc`, …). The consent journey instead
 * returns ONE aggregated portal extract: `gstr.details` — a flat list of ~39
 * single-key objects covering turnover, month-wise sales/purchase, filing
 * compliance, HSN and counterparties.
 *
 * Reusing the upload component here rendered every figure as ₹0, because none of
 * those keys exist in this payload. Hence a separate component that reads
 * `details[]` directly.
 */
const GlibGstReturnFetchOutputComponent = ({ reportData, hideCredits, isSharedView }) => {
    const shouldHideCredits = hideCredits || isSharedView;
    const [expanded, setExpanded] = useState(false);
    const [downloading, setDownloading] = useState(false);

    const json = useMemo(() => {
        const rj = reportData?.report_json;
        if (!rj) return {};
        if (typeof rj !== 'string') return rj;
        try { return JSON.parse(rj); } catch { return {}; }
    }, [reportData]);

    const meta = useMemo(() => {
        if (!reportData) return null;
        return {
            isComplete: reportData.is_complete ?? (json.stage === 'completed'),
            workOrderId: reportData.work_order_id ?? json.work_order_id ?? null,
            excelUrl: (reportData.download_urls ?? json.download_urls ?? {})?.excel ?? null,
            remaining: reportData.remaining_credits ?? undefined,
        };
    }, [reportData, json]);

    // The consent flow yields a single aggregated record, not one per month.
    const gstr = useMemo(() => {
        const returns = json.returns ?? reportData?.returns ?? [];
        return Array.isArray(returns) && returns.length > 0 ? (returns[0].gstr ?? {}) : {};
    }, [json, reportData]);

    // details[] is a list of single-key objects — flatten to a lookup so
    // sections can be read by name instead of by index.
    const d = useMemo(() => {
        const out = {};
        (gstr.details ?? []).forEach((entry) => {
            if (entry && typeof entry === 'object') {
                Object.entries(entry).forEach(([k, v]) => { out[k] = v; });
            }
        });
        return out;
    }, [gstr]);

    const first = (v) => (Array.isArray(v) && v.length > 0 ? v[0] : null);

    // Memoised together: these feed the chart/compliance useMemos below, so
    // deriving them inline would hand those a fresh object reference on every
    // render and defeat the memoisation entirely (see ui-ux P14).
    const s = useMemo(() => ({
        metadata: gstr.metadata ?? {},
        basic:    first(d['Basic Information']) ?? {},
        salesAgg: first(d['Sales Aggregate Turnover']) ?? {},
        purchAgg: first(d['Purchase Aggregate Turnover']) ?? {},
        salesMon: first(d['Sales Summary']) ?? {},
        purchMon: first(d['Purchase Summary']) ?? {},
        variance: first(d['Sales vs Purchase Variance Yearly']) ?? {},
        filing:   d['Customer Filing Data'] ?? {},
        taxTrend: Array.isArray(d['Total Tax Payment Trend']) ? d['Total Tax Payment Trend'] : [],
    }), [gstr, d]);

    const { metadata, basic, salesAgg, purchAgg, salesMon, purchMon, variance, filing, taxTrend } = s;

    const salesTotal = parseFloat(salesAgg.Total ?? 0) || 0;
    const purchTotal = parseFloat(purchAgg.Total ?? 0) || 0;
    const monthCount = Object.keys(salesMon).length;
    // "Avg Monthly Sales" = the average of the REAL per-month "Sales Summary"
    // values (Apr, May, Jun…), NOT Aggregate Total ÷ months. The Aggregate Total
    // spans the whole multi-year period, so Total÷12 overstates the monthly figure
    // and would not match the month-wise turnover used for eligibility. Averaging
    // the Sales Summary months keeps the tile, the prefilled field, and the
    // eligibility table on the SAME number.
    const salesMonSum = useMemo(
        () => Object.values(salesMon).reduce((acc, v) => acc + (parseFloat(String(v).replace(/[^0-9.-]/g, '')) || 0), 0),
        [salesMon],
    );
    const avgMonthly = monthCount > 0 ? salesMonSum / monthCount : 0;

    // Filing compliance: how many months were filed across every return type.
    const compliance = useMemo(() => {
        let filed = 0;
        let total = 0;
        Object.values(filing || {}).forEach((months) => {
            Object.values(months || {}).forEach((status) => {
                total += 1;
                if (String(status).toLowerCase() === 'filed') filed += 1;
            });
        });
        return { filed, total, pct: total > 0 ? Math.round((filed / total) * 100) : null };
    }, [filing]);

    const salesVsPurchaseChart = useMemo(() => {
        // Labels carry the YEAR (Apr-2024 … Mar-2025) from the filing period, in
        // fiscal-year order — matching the eligibility modal + the vendor's own
        // month-year tables. Values are still looked up by the bare month key.
        const cols = fyMonthsWithYear(salesMon, d.FromPeriod);
        if (cols.length === 0) return null;
        return {
            labels: cols.map((c) => c.label),
            datasets: [
                {
                    label: 'Sales',
                    data: cols.map((c) => parseFloat(salesMon[c.key]) || 0),
                    backgroundColor: 'var(--green-400)',
                },
                {
                    label: 'Purchases',
                    data: cols.map((c) => parseFloat(purchMon[c.key]) || 0),
                    backgroundColor: 'var(--blue-400)',
                },
            ],
        };
    }, [salesMon, purchMon, d.FromPeriod]);

    const yoyChart = useMemo(() => {
        const trend = d['Previous Year vs Current Year Sales Trend'];
        if (!trend || typeof trend !== 'object') return null;
        const periods = Object.keys(trend);
        if (periods.length === 0) return null;
        const months = sortFyMonths(trend[periods[0]]);
        const colours = ['var(--primary-color)', 'var(--green-500)'];
        return {
            labels: months,
            datasets: periods.slice(0, 2).map((p, i) => ({
                label: p,
                data: months.map((m) => parseFloat(trend[p]?.[m]) || 0),
                borderColor: colours[i],
                backgroundColor: 'transparent',
                tension: 0.35,
                fill: false,
            })),
        };
    }, [d]);

    if (!reportData) return <NoRecordsCard subtitle="No GST data available" />;

    const hasData = Object.keys(d).length > 0;

    const downloadExcel = () => {
        if (!meta?.excelUrl) return;
        setDownloading(true);
        window.open(meta.excelUrl, '_blank', 'noopener,noreferrer');
        setTimeout(() => setDownloading(false), 800);
    };

    return (
        <>
            <Card className="mb-3">
                <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center gap-2 mb-3">
                    <h2 className="text-lg font-semibold text-900 m-0">
                        <i className="pi pi-percentage mr-2 text-primary" aria-hidden="true" />
                        GST Portal Summary
                    </h2>
                    <div className="flex flex-wrap align-items-center gap-2">
                        {basic['Taxpayer Type'] && (
                            <Tag severity="info" value={basic['Taxpayer Type']} className="text-sm" />
                        )}
                        {basic['GSTIN/UIN Status'] && (
                            <Tag
                                severity={String(basic['GSTIN/UIN Status']).toLowerCase() === 'active' ? 'success' : 'danger'}
                                icon={String(basic['GSTIN/UIN Status']).toLowerCase() === 'active' ? 'pi pi-check-circle' : 'pi pi-times-circle'}
                                value={basic['GSTIN/UIN Status']}
                                className="text-sm"
                            />
                        )}
                        {!shouldHideCredits && meta?.remaining !== undefined && meta.remaining !== null && (
                            <Tag severity="success" icon="pi pi-wallet" value={`Remaining: ${meta.remaining}`} className="text-sm" />
                        )}
                    </div>
                </div>

                {/* Taxpayer identity */}
                <div className="grid mb-2">
                    <InfoItem label="GSTIN" value={metadata.gstin || basic['GSTIN/UIN']} />
                    <InfoItem label="Trade Name" value={metadata.trade_name || basic['Registration Trade Name']} />
                    <InfoItem label="Legal Name" value={metadata.legal_name || basic['Legal Name']} />
                    <InfoItem
                        label="Period Covered"
                        value={d.FromPeriod || d.ToPeriod
                            ? `${formatPeriod(d.FromPeriod)} – ${formatPeriod(d.ToPeriod)}`
                            : null}
                    />
                </div>

                {/* Headline figures */}
                <div className="grid">
                    <StatTile
                        icon="pi-arrow-up"
                        label="Total Sales Turnover"
                        value={formatINR(salesTotal)}
                        sub={monthCount ? `Across ${monthCount} month(s)` : 'Aggregate turnover'}
                    />
                    <StatTile
                        icon="pi-arrow-down"
                        label="Total Purchases"
                        value={formatINR(purchTotal)}
                        sub="Inward supplies"
                    />
                    <StatTile
                        icon="pi-calendar"
                        label="Avg Monthly Sales"
                        value={formatINR(avgMonthly)}
                        sub="Turnover / months"
                    />
                    <StatTile
                        icon="pi-check-circle"
                        label="Filing Compliance"
                        value={compliance.pct === null ? '—' : `${compliance.pct}%`}
                        sub={compliance.total ? `${compliance.filed} of ${compliance.total} returns filed` : 'No filing data'}
                        severity={compliance.pct === 100 ? 'success' : (compliance.pct !== null && compliance.pct < 80 ? 'danger' : undefined)}
                    />
                </div>

                {hasData && (
                    <div className="flex justify-content-center mt-3">
                        <Button
                            label={expanded ? 'Hide detailed analysis' : 'View detailed analysis'}
                            icon={expanded ? 'pi pi-chevron-up' : 'pi pi-chevron-down'}
                            iconPos="right"
                            text
                            onClick={() => setExpanded((v) => !v)}
                            aria-expanded={expanded}
                            className="w-full md:w-auto"
                        />
                    </div>
                )}
            </Card>

            {!hasData ? (
                <Card>
                    <Message
                        severity="warn"
                        className="w-full"
                        content={
                            <div className="text-sm">
                                <div className="font-medium mb-1">No GST data returned</div>
                                <div className="text-700">
                                    The consent completed but the provider returned no portal data. Download
                                    the Excel workbook, or run the fetch again.
                                </div>
                            </div>
                        }
                    />
                </Card>
            ) : expanded ? (
                <Card>
                    <TabView renderActiveOnly scrollable>
                        {/* ---------------------------------------- OVERVIEW */}
                        <TabPanel header="Overview" leftIcon="pi pi-chart-bar mr-2">
                            <Panel header="Registration Details" className="mb-3">
                                <div className="grid">
                                    <InfoItem label="Constitution" value={basic['Constitution of Business']} />
                                    <InfoItem label="Nature of Business" value={basic['Nature of Business Activity']} />
                                    <InfoItem label="Date of Registration" value={basic['Date of Registration']} />
                                    <InfoItem label="State Jurisdiction" value={basic['State Jurisdiction']} />
                                </div>
                            </Panel>

                            <Panel header="Sales vs Purchases" className="mb-3">
                                <div className="grid">
                                    <InfoItem label="Total Sales" value={variance.Sales ?? salesTotal} money />
                                    <InfoItem label="Total Purchases" value={variance.Purchase ?? purchTotal} money />
                                    <InfoItem label="Gross Margin" value={salesTotal - purchTotal} money />
                                    <InfoItem
                                        label="Margin %"
                                        value={salesTotal > 0 ? `${(((salesTotal - purchTotal) / salesTotal) * 100).toFixed(1)}%` : null}
                                    />
                                </div>
                            </Panel>

                            {salesVsPurchaseChart && (
                                <Panel header="Monthly Sales vs Purchases" className="mb-3">
                                    <Chart type="bar" data={salesVsPurchaseChart} options={chartOptions()} style={{ height: '320px' }} />
                                </Panel>
                            )}

                            {yoyChart && (
                                <Panel header="Year-on-Year Sales Trend" className="mb-3">
                                    <Chart type="line" data={yoyChart} options={chartOptions()} style={{ height: '320px' }} />
                                </Panel>
                            )}
                        </TabPanel>

                        {/* ------------------------------------------- SALES */}
                        <TabPanel header="Sales" leftIcon="pi pi-arrow-up mr-2">
                            <Panel header="Aggregate Turnover" className="mb-3">
                                <div className="grid">
                                    {Object.entries(salesAgg).map(([k, v]) => (
                                        <InfoItem key={k} label={k} value={v} money />
                                    ))}
                                </div>
                            </Panel>
                            {Array.isArray(d['GST Tax Rate Wise Sales Bifurcation']) && (
                                <Panel header="Tax Rate Wise Sales" className="mb-3" toggleable>
                                    <AutoTable rows={d['GST Tax Rate Wise Sales Bifurcation']} />
                                </Panel>
                            )}
                            {Array.isArray(d['State Wise Sales Bifurcation']) && (
                                <Panel header="State Wise Sales" className="mb-3" toggleable>
                                    <AutoTable rows={d['State Wise Sales Bifurcation']} />
                                </Panel>
                            )}
                            {Array.isArray(d['Top 10 Customers Against Total Sales']) && (
                                <Panel header="Customers" className="mb-3" toggleable collapsed>
                                    <AutoTable
                                        rows={d['Top 10 Customers Against Total Sales']}
                                        note={`${formatNum(d['Top 10 Customers Against Total Sales'].length)} counterparties reported by the portal.`}
                                    />
                                </Panel>
                            )}
                        </TabPanel>

                        {/* --------------------------------------- PURCHASES */}
                        <TabPanel header="Purchases" leftIcon="pi pi-arrow-down mr-2">
                            <Panel header="Aggregate Purchases" className="mb-3">
                                <div className="grid">
                                    {Object.entries(purchAgg).map(([k, v]) => (
                                        <InfoItem key={k} label={k} value={v} money />
                                    ))}
                                </div>
                            </Panel>
                            {Array.isArray(d['GST Tax Rate Wise Purchase Bifurcation']) && (
                                <Panel header="Tax Rate Wise Purchases" className="mb-3" toggleable>
                                    <AutoTable rows={d['GST Tax Rate Wise Purchase Bifurcation']} />
                                </Panel>
                            )}
                            {Array.isArray(d['State Wise Purchase Bifurcation']) && (
                                <Panel header="State Wise Purchases" className="mb-3" toggleable>
                                    <AutoTable rows={d['State Wise Purchase Bifurcation']} />
                                </Panel>
                            )}
                            {Array.isArray(d['Top 10 Sellers Against Total Purchase']) && (
                                <Panel header="Suppliers" className="mb-3" toggleable collapsed>
                                    <AutoTable
                                        rows={d['Top 10 Sellers Against Total Purchase']}
                                        note={`${formatNum(d['Top 10 Sellers Against Total Purchase'].length)} counterparties reported by the portal.`}
                                    />
                                </Panel>
                            )}
                        </TabPanel>

                        {/* -------------------------------------- COMPLIANCE */}
                        <TabPanel header="Compliance" leftIcon="pi pi-verified mr-2">
                            {Object.keys(filing).length > 0 && (
                                <Panel header="Return Filing Status" className="mb-3">
                                    {Object.entries(filing).map(([returnType, months]) => (
                                        <div key={returnType} className="mb-3">
                                            <div className="text-900 text-sm font-semibold mb-2">{returnType}</div>
                                            <div className="flex flex-wrap gap-2">
                                                {sortFyMonths(months).map((m) => {
                                                    const status = String(months[m] || '');
                                                    const filed = status.toLowerCase() === 'filed';
                                                    return (
                                                        <Tag
                                                            key={m}
                                                            severity={filed ? 'success' : 'warning'}
                                                            icon={filed ? 'pi pi-check' : 'pi pi-exclamation-triangle'}
                                                            value={`${m}: ${status || 'N/A'}`}
                                                            className="text-xs"
                                                        />
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    ))}
                                </Panel>
                            )}
                            {taxTrend.length > 0 && (
                                <Panel header="Tax Payment Trend" className="mb-3">
                                    <AutoTable
                                        rows={taxTrend}
                                        moneyKeys={['Interest', 'Late Fees', 'Paid Through ITC', 'Paid Through Cash']}
                                    />
                                </Panel>
                            )}
                            {Array.isArray(d['PAN Level GSTIN Registration Bifurcation']) && (
                                <Panel header="Other GSTINs on this PAN" className="mb-3" toggleable>
                                    <AutoTable rows={d['PAN Level GSTIN Registration Bifurcation']} />
                                </Panel>
                            )}
                        </TabPanel>

                        {/* --------------------------------------------- HSN */}
                        <TabPanel header="HSN" leftIcon="pi pi-tags mr-2">
                            {Array.isArray(d['HSN Summary']) && (
                                <Panel header="HSN Summary" className="mb-3">
                                    <AutoTable rows={d['HSN Summary']} />
                                </Panel>
                            )}
                            {Array.isArray(d['HSN Summary Code Wise Bifurcation(sales)']) && (
                                <Panel header="HSN Code Wise (Sales)" className="mb-3" toggleable>
                                    <AutoTable rows={d['HSN Summary Code Wise Bifurcation(sales)']} />
                                </Panel>
                            )}
                            {Array.isArray(d['HSN Per Unit Price (sales)']) && (
                                <Panel header="HSN Per Unit Price" className="mb-3" toggleable collapsed>
                                    <AutoTable rows={d['HSN Per Unit Price (sales)']} />
                                </Panel>
                            )}
                        </TabPanel>
                    </TabView>

                    {meta?.workOrderId && (
                        <p className="text-500 text-xs mt-3 mb-0">
                            Provider reference: <span className="font-mono">{meta.workOrderId}</span>
                        </p>
                    )}
                </Card>
            ) : null}

            {meta?.excelUrl && (
                <Card className="mt-3">
                    <div className="flex flex-column md:flex-row md:align-items-center gap-3">
                        <div className="flex-1 min-w-0">
                            <div className="text-900 text-sm font-semibold mb-1">
                                <i className="pi pi-file-excel mr-2 text-green-600" aria-hidden="true" />
                                Download the full report
                            </div>
                            <div className="text-600 text-xs">
                                Complete GST portal extract, including any rows trimmed from the tables above.
                            </div>
                        </div>
                        <Button
                            label="Download Excel"
                            icon="pi pi-download"
                            severity="success"
                            loading={downloading}
                            onClick={downloadExcel}
                            className="w-full md:w-auto"
                        />
                    </div>
                </Card>
            )}
        </>
    );
};

export default GlibGstReturnFetchOutputComponent;
